import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@/components/DesignSystem/Icons';
import { CareerPathwayDropdownProps, Option } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';

const CareerPathwayDropdown: React.FC<CareerPathwayDropdownProps> = ({
  label,
  options,
  selectedOption,
  onOptionChange,
  placeholder,
  getOptionValue,
  getOptionDisplay,
  className = '',
  isEditable = false,
  viewCorrelationId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [currentPath, setCurrentPath] = useState<Option[]>([]);
  const [currentOptions, setCurrentOptions] = useState(options);

  // Filter options based on search term
  const filteredOptions = currentOptions.filter((option) =>
    getOptionDisplay(option).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleOptionClick = (option: Option) => {
    if (option.nestedOptions) {
      setCurrentPath([...currentPath, option]);
      setCurrentOptions(option.nestedOptions);
      setSearchTerm('');
    } else if (isEditable) {
      onOptionChange(option);

      // Determine fieldName based on label
      let fieldName: string;
      if (label.toLowerCase() === 'current education') {
        fieldName = 'degree';
      } else if (label.toLowerCase() === 'field of study') {
        fieldName = 'subject';
      } else {
        fieldName = label.toLowerCase().replace(' ', '-');
      }

      tagularEvent('FieldSelected', {
        correlationId: viewCorrelationId,
        formContext: {
          formId: '',
          formName: 'career-pathways-dcs',
          formType: 'career-pathways',
          formVersion: '',
        },
        userInputField: {
          fieldName: fieldName,
          fieldLabel: label,
          fieldValue: getOptionDisplay(option),
          fieldType: 'select',
        },
      });
      setIsOpen(false);
      setSearchTerm('');
      setCurrentPath([]);
      setCurrentOptions(options);
    }
  };

  const handleCardClick = () => {
    if (isEditable) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setCurrentPath([]);
        setCurrentOptions(options);
      }
    }
  };

  const handleBackClick = () => {
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      setCurrentOptions(
        newPath.length === 0
          ? options
          : newPath[newPath.length - 1].nestedOptions || [],
      );
      setSearchTerm('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent dropdown from closing when typing
    e.stopPropagation();

    // Handle Enter key to select first option
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleOptionClick(filteredOptions[0]);
    }

    // Handle Escape key to close dropdown
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const selectedDisplay = (() => {
    if (!selectedOption) return placeholder;

    // Try to find the option in the current options array by matching a unique property
    const matchingOption = options.find(
      (option) => getOptionValue(option) === getOptionValue(selectedOption),
    );

    return matchingOption
      ? getOptionDisplay(matchingOption)
      : getOptionDisplay(selectedOption);
  })();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {isEditable && (
        <div className='font-bold text-sm text-gray-900 pb-1'>{label}</div>
      )}
      <div
        className={` border border-gray-200 rounded-lg transition-all duration-200 ${
          !isEditable
            ? 'bg-gray-50 cursor-default px-3 py-2'
            : 'bg-white cursor-pointer hover:shadow-sm h-11 flex px-4 py-2 items-center'
        }`}
        onClick={handleCardClick}
      >
        {!isEditable && (
          <div className='font-bold text-gray-900 text-sm overflow-hidden whitespace-nowrap text-ellipsis'>
            {label}
          </div>
        )}

        <div className='flex items-center justify-between w-full'>
          <div
            className={`text-sm overflow-hidden whitespace-nowrap text-ellipsis flex-1 mr-2 ${selectedOption ? 'text-gray-700 font-medium' : 'text-gray-500'}`}
          >
            {selectedDisplay}
          </div>
          {isEditable && (
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          )}
        </div>
      </div>

      {isEditable && isOpen && (
        <div className='absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-hidden'>
          {/* Navigation Header */}
          {currentPath.length > 0 && (
            <div className='p-2 border-b border-gray-200 flex items-center'>
              <button
                onClick={handleBackClick}
                className='flex items-center text-gray-600 hover:text-gray-900 text-sm font-medium'
              >
                <ChevronRightIcon className='w-4 h-4 mr-1 rotate-180' />
                Back to {getOptionDisplay(currentPath[currentPath.length - 1])}
              </button>
            </div>
          )}

          {/* Search Input */}
          <div className='p-3 border-b border-gray-200'>
            <input
              ref={searchInputRef}
              type='text'
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
            />
          </div>

          {/* Options List */}
          <div className='max-h-48 overflow-y-auto'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                    selectedOption &&
                    getOptionValue(selectedOption) === getOptionValue(option)
                      ? 'bg-blue-50 text-blue-700'
                      : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  <span className='flex items-center justify-between w-full'>
                    {getOptionDisplay(option)}
                    {option.nestedOptions && (
                      <div className='flex items-center gap-1'>
                        <span className='text-xxs text-gray-500 bg-gray-50 p-1 rounded-md'>
                          {option.nestedOptions.length}
                        </span>
                        <ChevronRightIcon className='w-4 h-4 text-gray-500' />
                      </div>
                    )}
                  </span>
                </div>
              ))
            ) : (
              <div className='px-4 py-3 text-sm text-gray-500 italic'>
                No {label.toLowerCase()} found matching `{searchTerm}`
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerPathwayDropdown;
